import { useCallback, useDeferredValue, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getAccount, getUserSettings } from '@/api';
import { eventBetBuilderQuery, eventDetailsQuery } from '@/api/queries';
import {
  selectCustomOutcomeForEvent,
  selectOutcomeForEvent,
  useBetslipTicket,
  useCustomBetPricing,
  withBetslipSelectedState,
  withCustomBetOutcomeState,
  getCustomBetAvailableSelections,
  subscribeCustomBetAvailability,
  clearCustomBetAvailableSelections,
} from '@/betslip';
import { isBetBuilderAvailableForEvent } from '@/domain/bet-builder';
import {
  POPULAR_MARKET_GROUP,
  filterEventMarkets,
  formatOutcomeLabel,
  isNextGoalMarket,
  mapEventDetails,
} from '@/domain/event-details';
import { usePrematchStatistics } from '@/hooks/usePrematchStatistics';
import { useTranslations } from '@/i18n/TranslationsProvider';
import { translateMarketLabel, formatMarketTitle } from '@/i18n/market-labels';
import { translateOutcomeLabel, sanitizeOutcomeDisplay } from '@/i18n/outcome-labels';
import { cn } from '@/lib/cn';
import {
  EmptyState,
  EventDetailNav,
  EventMatchHeader,
  MarketCard,
  MarketToolbar,
  Skeleton,
} from '@/ui';

export interface EventDetailsProps {
  eventId: string;
  sport: string;
  category: string;
  tournament: string;
  /** Deep-link from list BB CTA — opens Bet Builder tab when the event supports it. */
  initialOffer?: 'betbuilder';
}

type OfferMode = 'standard' | 'betbuilder';

function useCustomBetAvailability() {
  return useSyncExternalStore(
    subscribeCustomBetAvailability,
    getCustomBetAvailableSelections,
    () => null,
  );
}

export function EventDetails({
  eventId,
  sport,
  category,
  tournament,
  initialOffer,
}: EventDetailsProps) {
  const { t, i18n } = useTranslation();
  const { selectedLanguage, isLoadingTranslations } = useTranslations();
  const account = getAccount();
  const feedType = account?.FeedType;
  const betslipTicket = useBetslipTicket();
  const availableSelections = useCustomBetAvailability();
  const { refreshCustomBetPricing } = useCustomBetPricing();
  const { showStats } = usePrematchStatistics();
  const query = useQuery(eventDetailsQuery(eventId));
  const numericEventId = Number(eventId);
  const onShowStats = useCallback(() => {
    if (!Number.isFinite(numericEventId)) return;
    showStats(numericEventId);
  }, [numericEventId, showStats]);
  const [offerMode, setOfferMode] = useState<OfferMode>(
    initialOffer === 'betbuilder' ? 'betbuilder' : 'standard',
  );
  const [selectedTabId, setSelectedTabId] = useState(POPULAR_MARKET_GROUP);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [bbNotice, setBbNotice] = useState<string | null>(null);

  useEffect(() => {
    setOfferMode(initialOffer === 'betbuilder' ? 'betbuilder' : 'standard');
    setSelectedTabId(POPULAR_MARKET_GROUP);
    setSearchQuery('');
    setSearchOpen(false);
    setBbNotice(null);
    clearCustomBetAvailableSelections();
  }, [eventId, initialOffer]);

  const view = useMemo(
    () => (query.data ? mapEventDetails(query.data) : null),
    [query.data],
  );

  const showBetBuilder = isBetBuilderAvailableForEvent({
    account,
    user: getUserSettings(),
    betBuilderAvailable: view?.betBuilderAvailable ?? query.data?.BetBuilderAvailable,
    live: view?.header.type === 'live' || query.data?.Live === true,
  });

  const activeMode: OfferMode = showBetBuilder ? offerMode : 'standard';

  const bbQuery = useQuery({
    ...eventBetBuilderQuery(eventId),
    enabled: showBetBuilder && activeMode === 'betbuilder',
  });

  const bbView = useMemo(
    () => (bbQuery.data ? mapEventDetails(bbQuery.data) : null),
    [bbQuery.data],
  );

  const activeView = activeMode === 'betbuilder' ? bbView : view;

  const resolveMarketLabel = useMemo(() => {
    void selectedLanguage;
    void isLoadingTranslations;
    return (key: string) => translateMarketLabel(key, i18n, feedType);
  }, [feedType, i18n, isLoadingTranslations, selectedLanguage]);

  const resolveOutcomeLabel = useMemo(() => {
    void selectedLanguage;
    void isLoadingTranslations;
    return (code: string, specifier?: string, marketKey?: string, marketGroup?: string) => {
      const translated = translateOutcomeLabel(code, i18n, feedType);
      const marketLabel = marketKey ? translateMarketLabel(marketKey, i18n, feedType) : undefined;
      if (isNextGoalMarket(marketKey ?? '', marketGroup, marketLabel)) {
        return sanitizeOutcomeDisplay(translated);
      }
      return formatOutcomeLabel(translated, specifier);
    };
  }, [feedType, i18n, isLoadingTranslations, selectedLanguage]);

  const tabs = useMemo(() => {
    if (!activeView) return [];
    return activeView.tabs.map((tab) => ({
      id: tab.id,
      label:
        tab.id === POPULAR_MARKET_GROUP
          ? t('All', { defaultValue: 'All' })
          : resolveMarketLabel(tab.code),
    }));
  }, [activeView, resolveMarketLabel, t]);

  const visibleMarkets = useMemo(() => {
    if (!activeView) return [];
    const tabId = activeView.tabs.some((tab) => tab.id === selectedTabId)
      ? selectedTabId
      : POPULAR_MARKET_GROUP;
    const grouped = filterEventMarkets(activeView.markets, tabId);
    const needle = deferredSearchQuery.trim().toLowerCase();
    if (!needle) return grouped;
    return grouped.filter((market) => resolveMarketLabel(market.key).toLowerCase().includes(needle));
  }, [activeView, deferredSearchQuery, resolveMarketLabel, selectedTabId]);

  const setMode = (mode: OfferMode) => {
    setOfferMode(mode);
    setSelectedTabId(POPULAR_MARKET_GROUP);
    setSearchQuery('');
    setSearchOpen(false);
    setBbNotice(null);
    if (mode === 'standard') {
      clearCustomBetAvailableSelections();
    }
  };

  if (query.isPending) {
    return <EventDetailsSkeleton />;
  }

  if (query.isError || !view) {
    return (
      <EmptyState
        icon="warning"
        title="Could not load event"
        description="Try again in a moment."
      />
    );
  }

  const alternativeLabel = t('Alternative Options', { defaultValue: 'Alternative Options' });
  const header = view.header;

  return (
    <div className="flex flex-col gap-3">
      <EventDetailNav
        league={header.league}
        categoryName={header.category}
        categoryId={header.categoryId}
        tournamentName={header.tournament}
        tournamentId={header.tournamentId}
        crestSrc={header.crestSrc}
        sport={sport}
        category={category}
        tournament={tournament}
      />
      <EventMatchHeader
        home={header.home}
        away={header.away}
        type={header.type}
        kickoff={header.kickoff}
        date={header.date}
        clock={header.clock}
        homeScore={header.homeScore}
        awayScore={header.awayScore}
        onShowStats={header.type === 'prematch' ? onShowStats : undefined}
      />

      {showBetBuilder ? (
        <div
          className="flex w-fit items-center gap-2"
          role="tablist"
          aria-label={t('OfferMode', { defaultValue: 'Offer mode' })}
        >
          <OfferModeButton
            selected={activeMode === 'standard'}
            onClick={() => setMode('standard')}
            label={t('Markets', { defaultValue: 'Markets' })}
          />
          <OfferModeButton
            selected={activeMode === 'betbuilder'}
            onClick={() => setMode('betbuilder')}
            label={t('BetBuilder', { defaultValue: 'Bet Builder' })}
          />
        </div>
      ) : null}

      {activeMode === 'betbuilder' && bbQuery.isPending ? (
        <EventDetailsMarketsSkeleton />
      ) : activeMode === 'betbuilder' && (bbQuery.isError || !bbView) ? (
        <EmptyState
          icon="warning"
          title={t('CustomBetUnavailable', { defaultValue: 'Bet Builder temporarily unavailable' })}
          description={t('Try again in a moment.', { defaultValue: 'Try again in a moment.' })}
        />
      ) : (
        <>
          <MarketToolbar
            tabs={tabs}
            selectedTabId={
              activeView && activeView.tabs.some((tab) => tab.id === selectedTabId)
                ? selectedTabId
                : POPULAR_MARKET_GROUP
            }
            onSelectTab={setSelectedTabId}
            searchOpen={searchOpen}
            searchQuery={searchQuery}
            onToggleSearch={() =>
              setSearchOpen((open) => {
                if (open) setSearchQuery('');
                return !open;
              })
            }
            onSearchChange={setSearchQuery}
          />
          {bbNotice ? (
            <p className="text-body-s text-status-danger" role="status">
              {bbNotice}
            </p>
          ) : null}
          {visibleMarkets.length === 0 ? (
            <EmptyState
              icon="details"
              title={
                activeMode === 'betbuilder'
                  ? t('CustomBetUnavailable', { defaultValue: 'Bet Builder temporarily unavailable' })
                  : t('No markets', { defaultValue: 'No markets' })
              }
              description={
                activeMode === 'betbuilder'
                  ? t('NoBetBuilderMarkets', {
                      defaultValue: 'No Bet Builder markets are available for this event.',
                    })
                  : t('No markets match this filter.', {
                      defaultValue: 'No markets match this filter.',
                    })
              }
            />
          ) : (
            <div className="flex flex-col gap-3">
              {visibleMarkets.map((market) => (
                <MarketCard
                  key={market.id}
                  title={formatMarketTitle(resolveMarketLabel(market.key), {
                    hasAlternativeLines: market.lines.length > 1,
                    specifier:
                      market.lines.find((line) => line.main)?.specifier ??
                      market.lines[0]?.specifier,
                  })}
                  lines={market.lines.map((line) => {
                    const labeled = line.outcomes.map((outcome) => ({
                      ...outcome,
                      marketName: resolveMarketLabel(market.key),
                      outcome: resolveOutcomeLabel(
                        outcome.outcome,
                        line.specifier,
                        market.key,
                        market.group,
                      ),
                    }));
                    const withState =
                      activeMode === 'betbuilder'
                        ? withCustomBetOutcomeState(
                            betslipTicket,
                            view.eventId,
                            labeled,
                            availableSelections,
                          )
                        : withBetslipSelectedState(betslipTicket, view.eventId, labeled);
                    return { ...line, outcomes: withState };
                  })}
                  alternativeLabel={alternativeLabel}
                  onSelectOutcome={(outcome) => {
                    if (activeMode === 'betbuilder') {
                      const source = bbQuery.data ?? query.data;
                      if (!source) return;
                      const result = selectCustomOutcomeForEvent(source, outcome, account);
                      if (!result.ok) {
                        setBbNotice(customToggleMessage(t, result.reason));
                        return;
                      }
                      setBbNotice(null);
                      void refreshCustomBetPricing(view.eventId).then((pricing) => {
                        if (pricing.cannotCombine) {
                          setBbNotice(
                            t('CustomBetCannotCombine', {
                              defaultValue: 'These selections cannot be combined',
                            }),
                          );
                        }
                      });
                      return;
                    }
                    if (!query.data) return;
                    selectOutcomeForEvent(query.data, outcome, account);
                  }}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function OfferModeButton({
  selected,
  onClick,
  label,
}: {
  selected: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      onClick={onClick}
      className={cn(
        'rounded-md px-3 py-1.5 text-[12px] leading-4 font-bold transition-colors',
        selected
          ? 'bg-odds-selected text-odds-selected-text'
          : 'bg-transparent text-[#ffe080] hover:bg-bg-elevated',
      )}
    >
      {label}
    </button>
  );
}

function customToggleMessage(
  t: (key: string, options?: { defaultValue?: string }) => string,
  reason: string,
): string {
  switch (reason) {
    case 'straight_mixed':
      return t('CustomBetMixedWithSingle', {
        defaultValue: 'Cannot mix Bet Builder with a standard bet on the same event',
      });
    case 'max_custom_legs':
      return t('MaximumBetsCombi', { defaultValue: 'Maximum combinations are' }) + ' 1';
    case 'max_selections':
      return t('CustomBetTooManySelections', {
        defaultValue: 'Too many Bet Builder selections',
      });
    case 'max_combi':
      return t('MaximumBetsCombi', { defaultValue: 'Maximum combinations are' });
    default:
      return t('CustomBetInvalid', { defaultValue: 'Invalid Bet Builder selection' });
  }
}

function EventDetailsMarketsSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton kind="line" className="h-9 w-full" />
      <Skeleton kind="block" className="h-24 w-full" />
      <Skeleton kind="block" className="h-24 w-full" />
    </div>
  );
}

export function EventDetailsSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton kind="line" className="h-5 w-48" />
      <Skeleton kind="block" className="h-32 w-full" />
      <Skeleton kind="line" className="h-9 w-full" />
      <Skeleton kind="block" className="h-24 w-full" />
      <Skeleton kind="block" className="h-24 w-full" />
    </div>
  );
}
